import React, { useCallback, useState } from 'react';
import PhoneChat from './PhoneChat';
import { HOW_IT_WORKS_STEPS } from './constants';

/**
 * Mobile gets all three threads inline: a selector is easy to miss on a small
 * screen. Desktop gets one phone driven by the step list, because three animated
 * phones side by side read as noise on a wide screen.
 */
const HowItWorks: React.FC = () => {
  const [active, setActive] = useState(0);

  const advance = useCallback(() => {
    setActive((current) => (current + 1) % HOW_IT_WORKS_STEPS.length);
  }, []);

  const step = HOW_IT_WORKS_STEPS[active];

  return (
    <>
      {/* Mobile: three steps, three threads, nothing to discover. */}
      <div className="mt-12 space-y-16 lg:hidden">
        {HOW_IT_WORKS_STEPS.map((item, index) => (
          <div key={item.title} className="flex flex-col items-center text-center">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-aspen-vibrant-blue text-lg font-bold text-white shadow-md">
              {index + 1}
            </span>
            <h3 className="mt-4 text-xl font-semibold text-aspen-deep-eucalyptus dark:text-aspen-pearl">
              {item.title}
            </h3>
            <p className="mt-3 max-w-xs text-base text-aspen-slate-blue dark:text-aspen-gentle-teal">
              {item.description}
            </p>
            <div className="mt-8 flex justify-center">
              <PhoneChat
                messages={item.conversation}
                label={item.conversationLabel}
                timeLabel={item.timeLabel}
                statusTime={item.statusTime}
                className="w-60"
              />
            </div>
          </div>
        ))}
      </div>

      {/* Desktop: the steps are the controls. */}
      <div className="mt-16 hidden items-center gap-16 lg:grid lg:grid-cols-[minmax(0,1fr)_auto] xl:gap-24">
        <ol className="space-y-4">
          {HOW_IT_WORKS_STEPS.map((item, index) => {
            const isActive = index === active;
            return (
              <li key={item.title}>
                <button
                  type="button"
                  onClick={() => setActive(index)}
                  aria-current={isActive ? 'step' : undefined}
                  className={`group flex w-full items-start gap-4 rounded-2xl p-5 text-left transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-aspen-vibrant-blue focus-visible:ring-offset-2 ${
                    isActive
                      ? 'bg-white shadow-lg ring-2 ring-aspen-vibrant-blue dark:bg-aspen-night-sage'
                      : 'bg-aspen-warm-ivory/70 ring-1 ring-aspen-blush-clay/50 hover:-translate-y-0.5 hover:bg-white hover:shadow-md hover:ring-aspen-sage-teal/60 dark:bg-aspen-night-sage/40 dark:ring-aspen-moonlit-clay/50 dark:hover:bg-aspen-night-sage'
                  }`}
                >
                  <span
                    className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-base font-bold transition-colors ${
                      isActive
                        ? 'bg-aspen-vibrant-blue text-white shadow-md'
                        : 'bg-aspen-sage-teal/20 text-aspen-sage-teal group-hover:bg-aspen-sage-teal group-hover:text-white dark:bg-aspen-sage-teal/30 dark:text-aspen-gentle-teal'
                    }`}
                  >
                    {index + 1}
                  </span>

                  <span className="flex flex-col">
                    <span
                      className={`text-lg font-semibold sm:text-xl ${
                        isActive
                          ? 'text-aspen-deep-eucalyptus dark:text-aspen-pearl'
                          : 'text-aspen-deep-eucalyptus/75 dark:text-aspen-pearl/75'
                      }`}
                    >
                      {item.title}
                    </span>
                    <span
                      className={`mt-1.5 text-base leading-7 ${
                        isActive
                          ? 'text-aspen-slate-blue dark:text-aspen-gentle-teal'
                          : 'text-aspen-slate-blue/75 dark:text-aspen-gentle-teal/75'
                      }`}
                    >
                      {item.description}
                    </span>
                  </span>

                  {/* Resting cards say so out loud; the active one is already playing. */}
                  <span
                    className={`ml-auto flex flex-shrink-0 items-center gap-1.5 self-center whitespace-nowrap text-xs font-semibold transition-colors ${
                      isActive
                        ? 'text-aspen-vibrant-blue'
                        : 'text-aspen-sage-teal group-hover:text-aspen-deep-eucalyptus dark:text-aspen-gentle-teal'
                    }`}
                  >
                    {isActive ? (
                      <>
                        <span className="h-2 w-2 rounded-full bg-aspen-vibrant-green" />
                        Playing
                      </>
                    ) : (
                      <>
                        Watch
                        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-none stroke-current" strokeWidth={2.5}>
                          <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>

        <div className="flex justify-center xl:justify-end">
          <PhoneChat
            key={active}
            messages={step.conversation}
            label={step.conversationLabel}
            timeLabel={step.timeLabel}
            statusTime={step.statusTime}
            onFinished={advance}
            className="w-64"
          />
        </div>
      </div>
    </>
  );
};

export default HowItWorks;
